import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Rating,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Container,
  Tab,
  Tabs,
  Fade,
  Breadcrumbs,
  Link,
  Skeleton,
  Tooltip,
  useTheme,
  Card,
  CardMedia,
  Zoom,
  IconButton,
  useMediaQuery,
  Snackbar,
  alpha,
  Avatar,
  Stack,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Hotel as HotelIcon,
  Business as BusinessIcon,
  SquareFoot as SquareFootIcon,
  Link as LinkIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  FileUpload as FileUploadIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  History as HistoryIcon,
  CalendarMonth as CalendarIcon,
  Domain as DomainIcon,
  VerifiedUser as VerifiedUserIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  BarChart as BarChartIcon,
  MonetizationOn as MonetizationOnIcon,
} from '@mui/icons-material';

import { api } from '@services';
import { Hotel } from '@types';
import { API_CONFIG } from '../../../config/api';
import * as hotelService from '../services/hotelService';
import { useDataSync, type DataSyncEvent } from '../../../utils/dataSync';
import HotelForm from '@features/hotels/components/HotelForm';
import HotelOCRResults from '@features/hotels/components/HotelOCRResults';
import HotelAIAnalysis from '@features/hotels/components/HotelAIAnalysis';
import OwnerDetailsForm from '@features/hotels/components/OwnerDetailsForm';
import HotelAnalysisHistory from '@features/hotels/components/HotelAnalysisHistory';
import BasicInformationTab from '@features/hotels/components/BasicInformationTab';
import DocumentUploadTab from '@features/hotels/components/DocumentUploadTab';
import OwnerDetailsTab from '@features/hotels/components/OwnerDetailsTab';
import HotelFinancialsForm from '@features/hotels/components/HotelFinancialsForm';
import MarketResearch from '@features/marketresearch/components/MarketResearch';
import { TabPanelProps, HotelScenarioAnalysisProps } from '@types';
import { useHotel, useHotelUIState, useDocumentUpload } from '@features/hotels/hooks';
import { MetricHighlight } from '@features/marketresearch/components/sections/Section';

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`hotel-tabpanel-${index}`} {...other}>
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ pt: 3 }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

// Compact metric component for dashboard tiles
const QuickMetric: React.FC<{ label: string; value: string | number; color: 'primary' | 'secondary' | 'success' | 'warning' }> = ({ label, value, color }) => (
  <Box
    sx={(theme) => ({
      p: 1,
      borderRadius: 1,
      backgroundColor: alpha(theme.palette[color].main, 0.06),
      border: `1px solid ${alpha(theme.palette[color].main, 0.15)}`,
      textAlign: 'center',
      minWidth: 80,
    })}
  >
    <Typography
      variant="body2"
      sx={(theme) => ({
        fontWeight: 700,
        color: theme.palette[color].main,
        mb: 0.25,
        fontSize: '0.9rem',
        lineHeight: 1.2,
      })}
    >
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 500, fontSize: '0.7rem', lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

const HotelDetails: React.FC = () => {
  const { hotelId } = useParams<{ hotelId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  // Add refresh counter to force re-renders when needed
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [financialOverviewLoading, setFinancialOverviewLoading] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [manualRefreshAvailable, setManualRefreshAvailable] = useState(false);

  // Add debounced refresh mechanism to prevent rapid API calls
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);

  const {
    hotel,
    loading,
    error,
    notFound,
    fetchHotelData,
    setError
  } = useHotel(hotelId);

  const {
    isEditing,
    setIsEditing,
    tabValue,
    handleTabChange,
    setTabValue,
    snackbarOpen,
    snackbarMessage,
    showSnackbar,
    closeSnackbar
  } = useHotelUIState();

  const {
    isUploading,
    uploadDocument,
    success,
    setSuccess,
    setError: setUploadError
  } = useDocumentUpload(hotelId, (message) => {
    showSnackbar(message);
    fetchHotelData(true); // Use cache-busting when documents are uploaded
    setTabValue(3);
  });

  const handleBack = () => navigate(-1);

  const handleSuccess = (message: string = 'Changes saved successfully') => {
    fetchHotelData(true); // Use cache-busting when handling success to ensure fresh data
    showSnackbar(message);
  };

  const handleFileUpload = async (file: File) => {
    await uploadDocument(file,
      // @ts-ignore - This property is added by our custom handler
      file.documentType || 'Miscellaneous Document'
    );
  };

  const [calculatingValuation, setCalculatingValuation] = useState<boolean>(false);

  // Debounced refresh function to prevent rapid successive API calls
  const debouncedRefresh = useCallback(async (delay: number = 1000) => {
    const now = Date.now();

    // Prevent calling if we just refreshed recently (within 2 seconds)
    if (now - lastRefreshTime.current < 2000) {
      console.log('🔄 Skipping refresh - too soon since last call');
      return;
    }

    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Set a new timeout
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🔄 Executing debounced hotel data refresh...');
        lastRefreshTime.current = Date.now();

        const freshData = await fetchHotelData(true);
        if (freshData) {
          setRefreshCounter(prev => prev + 1);
          console.log('✅ Debounced refresh successful');
        } else {
          console.warn('⚠️ Debounced refresh returned null data');
        }
      } catch (error: any) {
        console.error('❌ Debounced refresh failed:', error);
        // Don't show error to user for background refreshes
      }
    }, delay);
  }, [fetchHotelData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Setup data sync to refresh hotel data when valuation changes
  const { notifyUpdate } = useDataSync(hotelId || '', (event: DataSyncEvent) => {
    console.log('HotelDetails: Received data sync event:', event);

    if (event.type === 'VALUATION_DATA_UPDATED' || event.type === 'FINANCIAL_DATA_UPDATED') {
      if (event.source !== 'HotelDetails') {
        console.log('HotelDetails: Data updated elsewhere, refreshing hotel data...', {
          eventType: event.type,
          eventSource: event.source,
          timestamp: event.timestamp,
          currentEstimatedValue: hotel?.estimated_value
        });

                // For financial data updates, use simplified refresh strategy
        if (event.type === 'FINANCIAL_DATA_UPDATED') {
          console.log('HotelDetails: Immediate refresh for financial update...');

          // Show loading indicator
          setFinancialOverviewLoading(true);

          // Simplified single refresh with better error handling
          const performSingleRefresh = async () => {
            try {
              console.log('HotelDetails: Performing single refresh for financial update');

              // Wait a moment for the backend to settle after valuation processing
              await new Promise(resolve => setTimeout(resolve, 2000));

              // Attempt to refresh hotel data with retry logic
              let freshHotelData = null;
              let retryCount = 0;
              const maxRetries = 3;

              while (!freshHotelData && retryCount < maxRetries) {
                try {
                  retryCount++;
                  console.log(`🔄 Attempt ${retryCount}/${maxRetries} to fetch hotel data...`);

                  freshHotelData = await fetchHotelData(true);

                  if (freshHotelData) {
                    break;
                  }

                  // Wait before retrying
                  if (retryCount < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                  }
                } catch (retryError: any) {
                  console.warn(`⚠️ Retry ${retryCount} failed:`, retryError.message);

                  // If this is the last retry, let it throw
                  if (retryCount === maxRetries) {
                    throw retryError;
                  }

                  // Wait before retrying
                  await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                }
              }

              if (freshHotelData) {
                console.log('✅ HotelDetails: Single refresh successful', {
                  freshDataEstimatedValue: freshHotelData.estimated_value,
                  freshDataLastValuationDate: freshHotelData.last_valuation_date,
                  currentStateEstimatedValue: hotel?.estimated_value,
                  currentStateLastValuationDate: hotel?.last_valuation_date,
                  dataWasUpdated: freshHotelData.estimated_value !== hotel?.estimated_value,
                  timestamp: new Date().toISOString()
                });

                setRefreshCounter(prev => prev + 1);
                setFinancialOverviewLoading(false);

                // Check if we got updated data
                const hasUpdatedData = freshHotelData.estimated_value !== hotel?.estimated_value;

                if (hasUpdatedData) {
                  console.log('✅ HotelDetails: Successfully received updated data');
                } else {
                  // If no updated data, offer manual refresh option
                  setManualRefreshAvailable(true);
                  setNetworkError('Valuation may still be processing. Click "Refresh Data" to check for updates.');

                  // Clear this message after 10 seconds
                  setTimeout(() => {
                    setNetworkError(null);
                    setManualRefreshAvailable(false);
                  }, 10000);
                }
              } else {
                throw new Error('Received null hotel data');
              }

            } catch (error: any) {
              console.error('❌ HotelDetails: Single refresh failed:', {
                error: error.message,
                code: error.code,
                timestamp: new Date().toISOString()
              });

              setFinancialOverviewLoading(false);

              // Show user-friendly error message
              setNetworkError('Your changes were saved successfully and the valuation has been updated. However, we\'re having trouble refreshing the display. Click "Refresh Data" to see the updated values.');
              setManualRefreshAvailable(true);

              // Clear error after 15 seconds
              setTimeout(() => {
                setNetworkError(null);
                setManualRefreshAvailable(false);
              }, 15000);
            }
          };

          // Execute the single refresh
          performSingleRefresh();
        } else if (event.type === 'VALUATION_DATA_UPDATED') {
          console.log('HotelDetails: Refreshing hotel data for valuation update...');

          // Use debounced refresh to prevent rapid API calls
          debouncedRefresh(1500);
        }
      }
    }
  });

  // Handle valuation calculation
  const handleCalculateValuation = async () => {
    if (!hotelId) return;

    setCalculatingValuation(true);
    try {
      // First, fetch comprehensive financial data to get available years
      const token = localStorage.getItem('token');
      const financialResponse = await api.get(`/hotels/${hotelId}/comprehensive-financials`);

      let startYear = new Date().getFullYear() - 2;
      let endYear = new Date().getFullYear();

      if (financialResponse.data) {
        const comprehensiveData = financialResponse.data;
        const availableYears = comprehensiveData.summary?.yearsOfData || [];

        if (availableYears.length > 0) {
          startYear = Math.min(...availableYears);
          endYear = Math.max(...availableYears);
          console.log('🔍 Using financial data years for valuation:', { startYear, endYear, availableYears });
        }
      }

      const response = await api.post(`/enhanced-valuation/${hotelId}`, {
          options: {
            startYear,
            endYear
          }
      });

            const result = response.data;
      console.log('🔍 Valuation API Response:', result);

      // Check for various possible response structures
      let estimatedValue = null;
      let success = false;

      // Try different response structure possibilities
      if (result.success && result.valuation) {
        // Original expected structure
        success = true;
        estimatedValue = result.valuation.aiEnhanced?.valuation?.estimatedValue ||
                        result.valuation.weightedValue?.value;
      } else if (result.valuation) {
        // Response has valuation but no success flag
        success = true;
        estimatedValue = result.valuation.aiEnhanced?.valuation?.estimatedValue ||
                        result.valuation.weightedValue?.value ||
                        result.valuation.estimatedValue; // Direct estimatedValue
      } else if (result.estimatedValue || result.estimated_value) {
        // Direct estimated value in response
        success = true;
        estimatedValue = result.estimatedValue || result.estimated_value;
      } else if (typeof result === 'object' && Object.keys(result).length > 0) {
        // Response exists but structure is unknown
        success = true;
        console.log('🔍 Unknown response structure, assuming success');
      }

      if (success) {
        // Refresh hotel data to get updated estimated_value with cache-busting
        try {
          console.log('🔄 Waiting briefly for valuation to propagate, then refreshing hotel data...');
          // Wait a moment for the valuation to be fully saved in the database
          await new Promise(resolve => setTimeout(resolve, 500));

                    console.log('🔄 Attempting to refresh hotel data after successful valuation...');
          const refreshedHotel = await fetchHotelData(true);

          if (refreshedHotel) {
            console.log('✅ Hotel data refreshed successfully after valuation', {
              newEstimatedValue: refreshedHotel.estimated_value,
              previousEstimatedValue: hotel?.estimated_value,
              lastValuationDate: refreshedHotel.last_valuation_date
            });
          } else {
            console.warn('⚠️ Hotel data refresh returned null after valuation (after retries)');
            // Show user-friendly message about refresh issue
            showSnackbar('Valuation was successful, but there was an issue refreshing the display. Please refresh the page to see updated values.');
          }
        } catch (refreshError: any) {
          console.error('❌ Hotel data refresh failed after valuation:', {
            error: refreshError.message,
            status: refreshError.response?.status,
            statusText: refreshError.response?.statusText,
            data: refreshError.response?.data,
            timestamp: new Date().toISOString()
          });

          // Provide specific error handling based on error type
          if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
            showSnackbar('Valuation was successful, but authentication expired. Please refresh the page to see updated values.');
          } else if (refreshError.response?.status === 404) {
            showSnackbar('Valuation was successful, but there was an issue finding the hotel data. Please refresh the page.');
          } else if (refreshError.code === 'ERR_NETWORK') {
            showSnackbar('Valuation was successful, but there was a network issue refreshing the display. Please check your connection and refresh the page.');
          } else {
            showSnackbar('Valuation was successful, but there was an issue refreshing the display. Please refresh the page to see updated values.');
          }

          // Don't throw here - valuation was successful even if refresh failed
        }

        // Notify other components that valuation data has been updated
        notifyUpdate('VALUATION_DATA_UPDATED', 'HotelDetails');

        if (estimatedValue && typeof estimatedValue === 'number') {
          const formattedValue = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
          }).format(estimatedValue);

          showSnackbar(
            hotel?.estimated_value
              ? `Valuation refreshed successfully! Updated estimated value: ${formattedValue}`
              : `Valuation calculated successfully! Estimated value: ${formattedValue}`
          );
        } else {
          showSnackbar(
            hotel?.estimated_value
              ? 'Valuation refreshed and saved successfully!'
              : 'Valuation calculated and saved successfully!'
          );
        }
      } else {
        console.error('🔍 Invalid response structure:', result);
        throw new Error('Invalid response from valuation service');
      }
    } catch (error: any) {
      console.error('Error calculating valuation:', error);

      // Provide detailed error information
      let errorMessage = 'Failed to calculate valuation. Please try again.';

      if (error.response) {
        // API responded with error status
        console.error('API Error Response:', error.response);
        const status = error.response.status;
        const data = error.response.data;

        if (status === 401) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (status === 404) {
          errorMessage = 'Valuation service not found. Please contact support.';
        } else if (status >= 500) {
          errorMessage = 'Server error during valuation calculation. Please try again later.';
        } else if (data?.message) {
          errorMessage = `Valuation error: ${data.message}`;
        } else if (data?.error) {
          errorMessage = `Valuation error: ${data.error}`;
        }
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error during valuation calculation. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = `Valuation error: ${error.message}`;
      }

      showSnackbar(errorMessage);
    } finally {
      setCalculatingValuation(false);
    }
  };

  if (isEditing && hotel) {
    return (
      <HotelForm
        mode="edit"
        initialData={hotel}
        onSuccess={() => {
          setIsEditing(false);
          handleSuccess('Hotel details updated successfully');
        }}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Skeleton variant="text" width={250} height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mt: 2, borderRadius: 1 }} />
        <Box sx={{ mt: 4 }}>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Skeleton variant="rectangular" width={isMedium ? '100%' : '30%'} height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={isMedium ? '100%' : '30%'} height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={isMedium ? '100%' : '30%'} height={100} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="70vh">
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            borderRadius: 1,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            maxWidth: 500,
            width: '100%',
            mb: 3
          }}
        >
          Hotel not found. The hotel may have been deleted or you may not have permission to view it.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant="contained"
          color="primary"
          sx={{
            mt: 2,
            borderRadius: 1,
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          Back to Hotels
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="70vh">
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 1,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            maxWidth: 500,
            width: '100%',
            mb: 3
          }}
        >
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant="contained"
          color="primary"
          sx={{
            mt: 2,
            borderRadius: 1,
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!hotel) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        pb: 8,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(180deg, ${alpha('#111927', 0.9)}, ${alpha('#111927', 1)})`
          : `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.background.paper, 1)})`,
      }}
    >
      <Box sx={{ pt: 3, pb: 2, px: { xs: 1, sm: 2, md: 3 } }}>
                {/* Network Error Notification */}
        {networkError && (
          <Alert
            severity={manualRefreshAvailable ? "info" : "warning"}
            sx={{ mb: 2 }}
            onClose={() => {
              setNetworkError(null);
              setManualRefreshAvailable(false);
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {manualRefreshAvailable && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={async () => {
                      setNetworkError(null);
                      setManualRefreshAvailable(false);
                      setFinancialOverviewLoading(true);

                      try {
                        const refreshedData = await fetchHotelData(true);
                        if (refreshedData) {
                          setRefreshCounter(prev => prev + 1);
                          showSnackbar('Hotel data refreshed successfully!');
                        } else {
                          showSnackbar('Unable to refresh data. The hotel may be temporarily unavailable.');
                        }
                      } catch (error: any) {
                        console.error('Manual refresh failed:', error);
                        showSnackbar('Unable to refresh data. Please check your connection and try again.');
                      } finally {
                        setFinancialOverviewLoading(false);
                      }
                    }}
                  >
                    Refresh Data
                  </Button>
                )}
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </Button>
              </Box>
            }
          >
            {networkError}
          </Alert>
        )}

        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: 2,
            '& .MuiBreadcrumbs-separator': {
              mx: 0.8
            },
            '& .MuiLink-root, & .MuiTypography-root': {
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main
              }
            }
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <DashboardIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="/hotels"
            onClick={(e) => {
              e.preventDefault();
              navigate('/hotels');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HotelIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Hotels
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
            {hotel.name}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            {hotel.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 500,
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                '&:hover': {
                  borderColor: theme.palette.text.secondary,
                  backgroundColor: alpha(theme.palette.divider, 0.04),
                },
              }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<MonetizationOnIcon />}
              onClick={() => navigate(`/hotels/${hotelId}/valuation`)}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 500,
                color: theme.palette.success.dark,
                borderColor: theme.palette.success.main,
                '&:hover': {
                  borderColor: theme.palette.success.dark,
                  backgroundColor: alpha(theme.palette.success.main, 0.04),
                },
              }}
            >
              Valuation Analysis
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 0.75,
                boxShadow: 1,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  boxShadow: 2,
                },
              }}
            >
              Edit Hotel
            </Button>
          </Box>
        </Box>

        {/* Property Overview Panel */}
        <Card
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <DomainIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '1rem' }}>
              Property Overview
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Grid container spacing={2} alignItems="center">
              {/* Basic Info - Left Side */}
              <Grid item xs={12} md={5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>
                    {hotel.name}
                  </Typography>
                  <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <LocationOnIcon color="action" sx={{ fontSize: '1rem', mt: 0.25 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.4, color: theme.palette.text.secondary }}>
                      {[hotel.address, hotel.city, hotel.state, hotel.zip_code, hotel.country].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Box display="flex" alignItems="center">
                      <Rating
                        value={hotel.star_rating}
                        readOnly
                        precision={0.5}
                        size="small"
                        sx={{ fontSize: '1rem' }}
                      />
                      <Typography variant="body2" ml={0.5} fontWeight={500} color="text.secondary">
                        {hotel.star_rating}
                      </Typography>
                    </Box>
                    <Chip
                      label={hotel.property_type || 'Not Specified'}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                      }}
                    />
                    {hotel.chain_affiliation && (
                      <Chip
                        label={hotel.chain_affiliation}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Key Financial Metrics - Right Side */}
              <Grid item xs={12} md={7}>
                <Grid container spacing={1.5}>
                  <Grid item xs={3} sm={2.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <HotelIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      <QuickMetric label="Rooms" value={hotel.num_rooms || '—'} color="primary" />
                    </Box>
                  </Grid>
                  <Grid item xs={3} sm={2.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: theme.palette.secondary.main }} />
                      <QuickMetric label="Built" value={hotel.year_built || '—'} color="secondary" />
                    </Box>
                  </Grid>
                  {hotel.lot_size && (
                    <Grid item xs={3} sm={2.4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <SquareFootIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                        <QuickMetric label="Lot Size" value={`${hotel.lot_size.toLocaleString()} sf`} color="warning" />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={3} sm={2.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <MonetizationOnIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <QuickMetric
                        label="$/Room"
                        value={hotel.estimated_value && hotel.num_rooms
                          ? `$${Math.round(hotel.estimated_value / hotel.num_rooms).toLocaleString()}`
                          : '—'
                        }
                        color="success"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={3} sm={2.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                      <QuickMetric
                        label="Cap Rate"
                        value="—"
                        color="secondary"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Financial Overview Panel */}
        <Card
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              backgroundColor: alpha(theme.palette.success.main, 0.05),
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MonetizationOnIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '1rem' }}>
                Financial Overview
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {hotel.estimated_value && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={calculatingValuation ? <CircularProgress size={16} /> : <MonetizationOnIcon />}
                  onClick={handleCalculateValuation}
                  disabled={calculatingValuation}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                    '&:disabled': {
                      opacity: 0.7,
                    },
                  }}
                >
                  {calculatingValuation ? 'Refreshing...' : 'Refresh Valuation'}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                endIcon={calculatingValuation ? <CircularProgress size={16} /> : <KeyboardArrowRightIcon />}
                onClick={hotel.estimated_value ? () => navigate(`/hotels/${hotelId}/valuation`) : handleCalculateValuation}
                disabled={calculatingValuation}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: theme.palette.success.dark,
                  borderColor: theme.palette.success.main,
                  '&:hover': {
                    borderColor: theme.palette.success.dark,
                    backgroundColor: alpha(theme.palette.success.main, 0.04),
                  },
                  '&:disabled': {
                    opacity: 0.7,
                  },
                }}
              >
                {calculatingValuation ? 'Calculating...' : (hotel.estimated_value ? 'View Detailed Valuation' : 'Calculate Valuation')}
              </Button>
            </Box>
          </Box>

                                        <Box
            key={`financial-overview-${hotel.estimated_value}-${hotel.last_valuation_date}-${refreshCounter}`}
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              position: 'relative',
              opacity: financialOverviewLoading ? 0.7 : 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            {/* Loading overlay */}
            {financialOverviewLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1,
                  borderRadius: 1
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            <Grid container spacing={2} alignItems="center">
              {/* Financial Analytics - Full Width */}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Estimated Value
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.success.dark,
                          fontSize: '1.5rem',
                          transition: 'all 0.3s ease-in-out'
                        }}
                      >
                        {hotel.estimated_value
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(hotel.estimated_value)
                          : 'Not Valued'}
                      </Typography>
                      {hotel.estimated_value && hotel.lot_size && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          ${Math.round(hotel.estimated_value / hotel.lot_size).toLocaleString()}/sq ft
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Last Valuation
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                        {hotel.last_valuation_date
                          ? new Date(hotel.last_valuation_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : 'Never'}
                      </Typography>
                      {hotel.last_valuation_date && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {Math.floor((Date.now() - new Date(hotel.last_valuation_date).getTime()) / (1000 * 60 * 60 * 24))} days ago
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Analysis Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={hotel.estimated_value ? "Complete" : "Pending"}
                          size="small"
                          color={hotel.estimated_value ? "success" : "warning"}
                          sx={{ fontWeight: 500 }}
                        />
                        {hotel.estimated_value && (
                          <Chip
                            label="Ready for Review"
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Card>

        <Box
          sx={{
            mb: 0,
            position: 'relative',
            zIndex: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                px: 2,
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              },
              '& .Mui-selected': {
                color: `${theme.palette.primary.main} !important`,
                fontWeight: 700,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab
              label="Property Information"
              icon={<HomeIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Owner Details"
              icon={<PersonIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Property Documents"
              icon={<FileUploadIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Document Analysis"
              icon={<DescriptionIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Local Market Data"
              icon={<BusinessIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Property AI Insights"
              icon={<PsychologyIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Analysis History"
              icon={<HistoryIcon sx={{ fontSize: '1.25rem' }} />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <BasicInformationTab hotel={hotel} isMobile={isMobile} />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <OwnerDetailsTab hotel={hotel} />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 1 }}
              icon={<FileUploadIcon />}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Property-Specific Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Upload documents specific to <strong>{hotel.name}</strong>. These will be used for property analysis and valuation.
              </Typography>
            </Alert>
            <DocumentUploadTab
              error={error}
              success={success}
              isUploading={isUploading}
              hotelId={hotelId || ''}
              onFileUpload={handleFileUpload}
              onErrorClose={() => setError(null)}
              onSuccessClose={() => setSuccess(null)}
            />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 1 }}
              icon={<DescriptionIcon />}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Financial Document Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    OCR extraction and financial data processing for <strong>{hotel.name}</strong> documents.
                  </Typography>
                </Box>
                <Chip
                  label="Ready for Analyst Review"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', fontWeight: 500, ml: 2 }}
                />
              </Box>
            </Alert>
            <HotelOCRResults hotelId={hotel.id} />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 1 }}
              icon={<PsychologyIcon />}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                AI-Powered Property Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Machine learning insights and recommendations specific to <strong>{hotel.name}</strong> based on uploaded documents and market data.
              </Typography>
            </Alert>
            <HotelAIAnalysis hotelId={hotel.id || ''} />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <HotelAnalysisHistory hotelId={hotel.id || ''} />
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 1 }}
              icon={<BusinessIcon />}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Local Market Analysis for {hotel.city}, {hotel.state}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Market data and comparable properties in the local area around <strong>{hotel.name}</strong>.
              </Typography>
            </Alert>
            {hotelId ? <MarketResearch hotelId={hotelId} /> : <Alert severity="error">Hotel ID is missing. Cannot load market research.</Alert>}
          </Paper>
        </TabPanel>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => closeSnackbar()}
        message={snackbarMessage}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }
        }}
      />
    </Box>
  );
};

export default HotelDetails;
